"use client"

import { Extension } from "@tiptap/core"
import TextAlign from "@tiptap/extension-text-align"
import { Markdown } from "@tiptap/markdown"
import { EditorContent, useEditor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import { Suggestion } from "@tiptap/suggestion"
import {
  AlignCenter,
  Bold,
  Code2,
  Italic,
  Link2,
  List,
  ListOrdered,
  Quote,
  Strikethrough,
} from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { Button } from "../atoms/Button"
import { cn } from "../utils"

type Variable = { value: string; label: string }

type Props = {
  value: string
  onChange: (value: string) => void
  variables: Variable[]
  disabled?: boolean
  onFocus?: () => void
  onBlur?: () => void
}

const createVariableSuggestion = (variables: Variable[]) =>
  Extension.create({
    name: "variableSuggestion",
    addProseMirrorPlugins() {
      return [
        Suggestion<Variable>({
          editor: this.editor,
          char: "{",
          allowedPrefixes: null,
          allow: ({ state, range }) =>
            state.doc.textBetween(range.from - 1, range.from, "\0", "\0") === "{",
          items: ({ query }) => {
            const normalizedQuery = query.trimStart().toLowerCase()
            return variables.filter(
              (variable) =>
                variable.value.toLowerCase().includes(normalizedQuery) ||
                variable.label.toLowerCase().includes(normalizedQuery),
            )
          },
          command: ({ editor, range, props }) => {
            editor.chain().focus().insertContentAt(range, `{ ${props.value} }}`).run()
          },
          render: () => {
            let element: HTMLDivElement | undefined
            let items: Variable[] = []
            let command: ((variable: Variable) => void) | undefined
            let selectedIndex = 0

            const renderItems = () => {
              if (!element) return
              const popup = element
              popup.replaceChildren()

              if (!items.length) {
                const empty = document.createElement("p")
                empty.className = "px-2 py-1.5 text-sm text-muted-foreground"
                empty.textContent = "No variables found"
                popup.append(empty)
                return
              }

              items.forEach((variable, index) => {
                const button = document.createElement("button")
                button.type = "button"
                button.className = cn(
                  "block w-full px-2 py-1.5 text-left text-sm",
                  index === selectedIndex && "bg-accent",
                )
                button.textContent = variable.label
                button.onmousedown = (event) => event.preventDefault()
                button.onclick = () => command?.(variable)
                popup.append(button)
              })
            }

            const position = (clientRect: (() => DOMRect | null) | null | undefined) => {
              const rect = clientRect?.()
              if (!element || !rect) return
              element.style.left = `${rect.left}px`
              element.style.top = `${rect.bottom + 4}px`
            }

            return {
              onStart: (props) => {
                element = document.createElement("div")
                element.className = "fixed z-50 w-64 rounded-md border bg-popover py-1 shadow-md"
                document.body.append(element)
                items = props.items
                command = props.command
                selectedIndex = 0
                renderItems()
                position(props.clientRect)
              },
              onUpdate: (props) => {
                items = props.items
                command = props.command
                selectedIndex = Math.min(selectedIndex, Math.max(items.length - 1, 0))
                renderItems()
                position(props.clientRect)
              },
              onKeyDown: ({ event }) => {
                if (!items.length) return false

                switch (event.key) {
                  case "ArrowDown":
                    selectedIndex = Math.min(selectedIndex + 1, items.length - 1)
                    renderItems()
                    return true
                  case "ArrowUp":
                    selectedIndex = Math.max(selectedIndex - 1, 0)
                    renderItems()
                    return true
                  case "Enter":
                  case "Tab":
                    const variable = items[selectedIndex]
                    if (variable) command?.(variable)
                    return true
                  default:
                    return false
                }
              },
              onExit: () => element?.remove(),
            }
          },
        }),
      ]
    },
  })

export function RichTextMarkdownEditor({
  value,
  onChange,
  variables,
  disabled = false,
  onFocus,
  onBlur,
}: Props) {
  const [isFocused, setIsFocused] = useState(false)
  const onChangeRef = useRef(onChange)
  const toolbarButtonClassName = "focus-visible:ring-0 focus-visible:ring-offset-0"
  const variableSuggestion = useMemo(() => createVariableSuggestion(variables), [variables])

  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Markdown,
      variableSuggestion,
    ],
    content: value,
    contentType: "markdown",
    editable: !disabled,
    onUpdate: ({ editor }) => onChangeRef.current(editor.getMarkdown()),
  })

  useEffect(() => {
    if (editor && value !== editor.getMarkdown()) {
      editor.commands.setContent(value, { contentType: "markdown", emitUpdate: false })
    }
  }, [editor, value])

  useEffect(() => {
    editor?.setEditable(!disabled)
  }, [disabled, editor])

  if (!editor) return null

  const run = (command: () => boolean) => {
    command()
    editor.commands.focus()
  }

  return (
    <div
      className={cn(
        "rounded-md border ring-offset-background",
        isFocused && "ring-2 ring-ring ring-offset-2",
      )}
      onFocus={(event) => {
        if (event.currentTarget.contains(event.relatedTarget)) return
        setIsFocused(true)
        onFocus?.()
      }}
      onBlur={(event) => {
        if (event.currentTarget.contains(event.relatedTarget)) return
        setIsFocused(false)
        onBlur?.()
      }}
    >
      <div className="flex flex-wrap gap-1 border-b p-1">
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className={toolbarButtonClassName}
          disabled={disabled}
          onClick={() => run(() => editor.chain().focus().toggleBold().run())}
        >
          <Bold className="size-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className={toolbarButtonClassName}
          disabled={disabled}
          onClick={() => run(() => editor.chain().focus().toggleItalic().run())}
        >
          <Italic className="size-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className={toolbarButtonClassName}
          disabled={disabled}
          onClick={() => run(() => editor.chain().focus().toggleStrike().run())}
        >
          <Strikethrough className="size-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className={toolbarButtonClassName}
          disabled={disabled}
          onClick={() => run(() => editor.chain().focus().toggleHeading({ level: 2 }).run())}
        >
          <Code2 className="size-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className={toolbarButtonClassName}
          disabled={disabled}
          onClick={() => run(() => editor.chain().focus().toggleBulletList().run())}
        >
          <List className="size-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className={toolbarButtonClassName}
          disabled={disabled}
          onClick={() => run(() => editor.chain().focus().toggleOrderedList().run())}
        >
          <ListOrdered className="size-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className={toolbarButtonClassName}
          disabled={disabled}
          onClick={() => run(() => editor.chain().focus().toggleBlockquote().run())}
        >
          <Quote className="size-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className={toolbarButtonClassName}
          disabled={disabled}
          onClick={() =>
            run(() =>
              editor.chain().focus().extendMarkRange("link").setLink({ href: "https://" }).run(),
            )
          }
        >
          <Link2 className="size-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className={toolbarButtonClassName}
          disabled={disabled}
          onClick={() =>
            run(() =>
              editor
                .chain()
                .focus()
                .setTextAlign(editor.isActive({ textAlign: "center" }) ? "left" : "center")
                .run(),
            )
          }
        >
          <AlignCenter className="size-4" />
        </Button>
      </div>
      <EditorContent
        editor={editor}
        className="min-h-44 p-3 text-sm [&_.tiptap]:min-h-38 [&_.tiptap]:outline-none"
      />
    </div>
  )
}
